import React, { useMemo } from 'react';
import {
  Box,
  Flex,
  Text,
  IconButton,
  Grid,
  Center,
  useColorModeValue
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';

// Days header: L M X J V S D (X for Wednesday to avoid clash with M=Tuesday)
const DAY_LABELS = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

// Returns 'YYYY-MM-DD' from a Date object
const toDateKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Returns a Date at midnight local time from 'YYYY-MM-DD'
const fromDateKey = (key) => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
};

function MiniCalendar({
  date,           // currently displayed month (Date object)
  selectedDate,   // currently selected date (Date object or null)
  highlightRange, // { start: Date, end: Date } to show range highlight
  onDayClick,     // (Date) => void
  onMonthChange,  // (Date) => void - called with first day of new month
  availableDates  // string[] of 'YYYY-MM-DD' that should show a dot indicator
}) {
  const displayDate = date || new Date();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Color tokens
  const headerColor = useColorModeValue('gray.700', 'gray.200');
  const dayLabelColor = useColorModeValue('gray.500', 'gray.400');
  const dayColor = useColorModeValue('gray.700', 'gray.200');
  const offMonthColor = useColorModeValue('gray.300', 'gray.600');
  const rangeBg = useColorModeValue('blue.50', 'blue.900');
  const dotColor = useColorModeValue('blue.500', 'blue.300');

  // Build calendar grid
  const calendarDays = useMemo(() => {
    const year = displayDate.getFullYear();
    const month = displayDate.getMonth();

    // First day of the month
    const firstDay = new Date(year, month, 1);
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);

    // Day of week of first day (0=Sun … 6=Sat), convert to Mon-based (0=Mon … 6=Sun)
    const firstDow = (firstDay.getDay() + 6) % 7; // Mon=0, Sun=6

    // Build array of 42 cells
    const cells = [];

    // Days from previous month
    for (let i = firstDow - 1; i >= 0; i--) {
      const d = new Date(year, month, -i);
      cells.push({ date: d, currentMonth: false });
    }

    // Days of current month
    for (let d = 1; d <= lastDay.getDate(); d++) {
      cells.push({ date: new Date(year, month, d), currentMonth: true });
    }

    // Fill remaining cells with next month days
    const remaining = 42 - cells.length;
    for (let d = 1; d <= remaining; d++) {
      cells.push({ date: new Date(year, month + 1, d), currentMonth: false });
    }

    return cells;
  }, [displayDate]);

  const availableSet = useMemo(() => new Set(availableDates || []), [availableDates]);

  const selectedKey = selectedDate ? toDateKey(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate())) : null;
  const todayKey = toDateKey(today);

  const rangeStart = highlightRange?.start ? new Date(highlightRange.start) : null;
  const rangeEnd = highlightRange?.end ? new Date(highlightRange.end) : null;
  if (rangeStart) rangeStart.setHours(0, 0, 0, 0);
  if (rangeEnd) rangeEnd.setHours(23, 59, 59, 999);

  const handlePrevMonth = () => {
    if (onMonthChange) {
      const d = new Date(displayDate.getFullYear(), displayDate.getMonth() - 1, 1);
      onMonthChange(d);
    }
  };

  const handleNextMonth = () => {
    if (onMonthChange) {
      const d = new Date(displayDate.getFullYear(), displayDate.getMonth() + 1, 1);
      onMonthChange(d);
    }
  };

  const monthLabel = displayDate.toLocaleDateString('es-CL', { month: 'long', year: 'numeric' });

  return (
    <Box userSelect="none" w="100%">
      {/* Month navigation */}
      <Flex align="center" justify="space-between" mb={2}>
        <IconButton
          icon={<ChevronLeftIcon />}
          size="xs"
          variant="ghost"
          aria-label="Mes anterior"
          onClick={handlePrevMonth}
        />
        <Text
          fontSize="xs"
          fontWeight="600"
          color={headerColor}
          textTransform="capitalize"
          textAlign="center"
        >
          {monthLabel}
        </Text>
        <IconButton
          icon={<ChevronRightIcon />}
          size="xs"
          variant="ghost"
          aria-label="Mes siguiente"
          onClick={handleNextMonth}
        />
      </Flex>

      {/* Day-of-week labels */}
      <Grid templateColumns="repeat(7, 1fr)" mb={1}>
        {DAY_LABELS.map((label) => (
          <Center key={label}>
            <Text fontSize="10px" fontWeight="600" color={dayLabelColor}>
              {label}
            </Text>
          </Center>
        ))}
      </Grid>

      {/* Calendar cells */}
      <Grid templateColumns="repeat(7, 1fr)" gap="1px">
        {calendarDays.map(({ date: cellDate, currentMonth }, idx) => {
          const key = toDateKey(cellDate);
          const isToday = key === todayKey;
          const isSelected = key === selectedKey;
          const isAvailable = availableSet.has(key);
          const inRange =
            rangeStart && rangeEnd && cellDate >= rangeStart && cellDate <= rangeEnd;

          let bgColor = 'transparent';
          let textColorVal = currentMonth ? dayColor : offMonthColor;
          let fontWeight = 'normal';

          if (isSelected) {
            bgColor = 'blue.500';
            textColorVal = 'white';
            fontWeight = '700';
          } else if (isToday) {
            bgColor = 'blue.500';
            textColorVal = 'white';
            fontWeight = '700';
          } else if (inRange) {
            bgColor = rangeBg;
          }

          return (
            <Center key={idx} flexDirection="column">
              <Box
                w="24px"
                h="24px"
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
                bg={bgColor}
                color={textColorVal}
                fontWeight={fontWeight}
                fontSize="11px"
                cursor={currentMonth ? 'pointer' : 'default'}
                opacity={currentMonth ? 1 : 0.4}
                _hover={
                  currentMonth && !isSelected && !isToday
                    ? { bg: 'blue.100', color: 'blue.700' }
                    : {}
                }
                onClick={() => {
                  if (currentMonth && onDayClick) {
                    onDayClick(cellDate);
                  }
                }}
                transition="background 0.15s"
              >
                {cellDate.getDate()}
              </Box>
              {/* Availability dot */}
              {isAvailable && !isSelected && !isToday && (
                <Box w="4px" h="4px" borderRadius="full" bg={dotColor} mt="-2px" />
              )}
              {!isAvailable && <Box w="4px" h="4px" mt="-2px" />}
            </Center>
          );
        })}
      </Grid>
    </Box>
  );
}

export default MiniCalendar;
